import { LoadingSpinner, SVGS } from '@zonofi/zono-money-design-system';
import React, { useEffect, useMemo, useState } from 'react';
import './payment-details-screen.scss';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  getCustomerInvite,
  PaymentStatusEnum,
  PaymentStatusNames,
  useAppSelector,
  useLazyGetPaymentDetailsByIdQuery,
} from '@zonofi/zono-money-store';
import {
  getInvoiceIcon,
  ProductDetailsGalleryView,
} from '@zonofi/zono-money-whatsapp-stack';
import moment from 'moment';
import { getFormattedAmount } from '@zonofi/common';
export const PaymentDetailsScreen = () => {
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const paymentId = params.paymentId;
  const workspaceId = searchParams.get('sellerWorkspaceId');
  const inviteDetails = useAppSelector(getCustomerInvite);
  const [getPaymentDetailsAPI, getPaymentDetailsAPIResponse] =
    useLazyGetPaymentDetailsByIdQuery();
  const [isViewReceipt, setIsViewReceipt] = useState(false);
  const [receiptURL, setReceiptURL] = useState<string>('');
  const [urlContentType, setUrlContentType] = useState<string>('');
  const imageUrlExtensions = useMemo(() => {
    return ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'];
  }, []);
  useEffect(() => {
    if (paymentId) {
      getPaymentDetailsAPI({
        includeSettlement: true,
        paymentId: String(paymentId),
        workspaceId: workspaceId,
        sellerWorkspaceId: workspaceId,
      });
    }
  }, [paymentId]);
  const icon = getInvoiceIcon(
    getPaymentDetailsAPIResponse.data?.status as PaymentStatusEnum
  );

  return (
    <div className="payment-details-screen">
      <div className="payment-details-screen-header font-primary-semibold">
        <img
          src={isViewReceipt ? SVGS.CloseIconBlack : SVGS.LeftArrowBlack}
          alt=""
          width={isViewReceipt ? 16 : 10}
          onClick={() => {
            if (isViewReceipt) {
              setIsViewReceipt(false);
              setReceiptURL('');
            } else {
              navigate(-1);
            }
          }}
        />
        {isViewReceipt ? 'View Image' : 'Payment Details'}
      </div>
      <div className="payment-details-screen-body">
        {getPaymentDetailsAPIResponse.isLoading ? (
          <div className="payment-details-screen-body-loading">
            <LoadingSpinner />
          </div>
        ) : !isViewReceipt ? (
          <>
            <div className="payment-details-screen-body-payment-info">
              <img src={icon} alt="" />
              <div className="payment-details-screen-body-payment-info-content">
                <div className="payment-details-screen-body-payment-info-content-payment-id font-primary-semibold">
                  {getPaymentDetailsAPIResponse.data?.paymentRefCode}
                </div>
                <div className="payment-details-screen-body-payment-info-content-payment-payed-by font-primary">
                  By {inviteDetails?.buyer?.name}
                </div>
              </div>
              <div
                className={`payment-list-item-top-badge font-primary-semibold`}
                style={{
                  alignSelf: 'center',
                }}
              >
                {
                  PaymentStatusNames[
                    getPaymentDetailsAPIResponse.data
                      ?.status as keyof typeof PaymentStatusNames
                  ]
                }
              </div>
            </div>
            {(getPaymentDetailsAPIResponse.data?.files?.length || 0) > 0 && (
              <div className="payment-details-screen-body-receipt">
                <div className="payment-details-screen-body-receipt-header">
                  <div className="payment-details-screen-body-receipt-header-primary font-primary-semibold">
                    VIEW PAYMENT RECEIPT
                  </div>
                  <div className="payment-details-screen-body-receipt-header-secondary font-primary">
                    View the uploaded payment receipt file
                  </div>
                </div>
                <div className="payment-details-screen-body-receipt-items">
                  {getPaymentDetailsAPIResponse.data?.files?.map(
                    (file, idx) => (
                      <div
                        key={idx}
                        className="payment-details-screen-body-receipt-items-item"
                        onClick={() => {
                          const cleanUrl = file?.url
                            ?.split('?')?.[0]
                            ?.toLowerCase();
                          let urlContent = '';
                          if (cleanUrl.endsWith('.pdf')) urlContent = 'pdf';
                          if (cleanUrl.match(/\.(jpeg|jpg|png|gif|webp|svg)$/))
                            urlContent = 'image';
                          setUrlContentType(urlContent);
                          setReceiptURL(file.url);
                          setIsViewReceipt(true);
                        }}
                      >
                        <img src={SVGS.CheckGreenIcon} alt="" width={12} />
                        <div className="payment-details-screen-body-receipt-items-item-name font-primary">
                          {file.fileName}
                        </div>
                        <img src={SVGS.EyeIcon} alt="" width={12} />
                      </div>
                    )
                  )}
                </div>
              </div>
            )}
            <div className="payment-details-screen-body-parsed-details">
              <div className="payment-details-screen-body-parsed-details-header">
                <div className="payment-details-screen-body-parsed-details-header-primary font-primary-semibold">
                  PARSED PAYMENT DETAILS
                </div>
                <div className="payment-details-screen-body-parsed-details-header-secondary font-primary">
                  The information below is pre-populated from the uploaded image
                </div>
              </div>
              <div className="payment-details-screen-body-parsed-details-items">
                {getPaymentDetailsAPIResponse.data?.paymentMode && (
                  <div className="payment-details-screen-body-parsed-details-items-item">
                    <div className="payment-details-screen-body-parsed-details-items-item-primary font-primary">
                      Payment Mode
                    </div>
                    <div className="payment-details-screen-body-parsed-details-items-item-secondary">
                      {getPaymentDetailsAPIResponse.data?.paymentMode}
                    </div>
                  </div>
                )}
                {getPaymentDetailsAPIResponse.data?.paymentDate && (
                  <div className="payment-details-screen-body-parsed-details-items-item">
                    <div className="payment-details-screen-body-parsed-details-items-item-primary font-primary">
                      Date & Time
                    </div>
                    <div className="payment-details-screen-body-parsed-details-items-item-secondary">
                      {moment(
                        getPaymentDetailsAPIResponse.data?.paymentDate
                      ).format('DD-MM-YYYY, hh:mm A')}
                    </div>
                  </div>
                )}
                {getPaymentDetailsAPIResponse.data?.amountPaid && (
                  <div className="payment-details-screen-body-parsed-details-items-item">
                    <div className="payment-details-screen-body-parsed-details-items-item-primary font-primary">
                      Amount Received
                    </div>
                    <div className="payment-details-screen-body-parsed-details-items-item-secondary">
                      {getFormattedAmount(
                        getPaymentDetailsAPIResponse.data?.amountPaid,
                        true
                      )}
                    </div>
                  </div>
                )}
                {getPaymentDetailsAPIResponse.data?.paymentRefCode && (
                  <div className="payment-details-screen-body-parsed-details-items-item">
                    <div className="payment-details-screen-body-parsed-details-items-item-primary font-primary">
                      Doc Number
                    </div>
                    <div className="payment-details-screen-body-parsed-details-items-item-secondary">
                      {getPaymentDetailsAPIResponse.data?.paymentRefCode}
                    </div>
                  </div>
                )}
                {getPaymentDetailsAPIResponse.data?.bankName && (
                  <div className="payment-details-screen-body-parsed-details-items-item">
                    <div className="payment-details-screen-body-parsed-details-items-item-primary font-primary">
                      Bank Name
                    </div>
                    <div className="payment-details-screen-body-parsed-details-items-item-secondary">
                      {getPaymentDetailsAPIResponse.data?.bankName}
                    </div>
                  </div>
                )}
                {getPaymentDetailsAPIResponse.data?.bankAccountNumber && (
                  <div className="payment-details-screen-body-parsed-details-items-item">
                    <div className="payment-details-screen-body-parsed-details-items-item-primary font-primary">
                      Bank Account Number
                    </div>
                    <div className="payment-details-screen-body-parsed-details-items-item-secondary">
                      {getPaymentDetailsAPIResponse.data?.bankAccountNumber}
                    </div>
                  </div>
                )}
                {getPaymentDetailsAPIResponse.data?.narration && (
                  <div className="payment-details-screen-body-parsed-details-items-item">
                    <div className="payment-details-screen-body-parsed-details-items-item-primary font-primary">
                      Narration
                    </div>
                    <div className="payment-details-screen-body-parsed-details-items-item-secondary">
                      {getPaymentDetailsAPIResponse.data?.narration}
                    </div>
                  </div>
                )}
                {getPaymentDetailsAPIResponse.data?.upiId && (
                  <div className="payment-details-screen-body-parsed-details-items-item">
                    <div className="payment-details-screen-body-parsed-details-items-item-primary font-primary">
                      UPI ID
                    </div>
                    <div className="payment-details-screen-body-parsed-details-items-item-secondary">
                      {getPaymentDetailsAPIResponse.data?.upiId}
                    </div>
                  </div>
                )}
                {getPaymentDetailsAPIResponse.data?.bankReference && (
                  <div className="payment-details-screen-body-parsed-details-items-item">
                    <div className="payment-details-screen-body-parsed-details-items-item-primary font-primary">
                      UTR Number
                    </div>
                    <div className="payment-details-screen-body-parsed-details-items-item-secondary">
                      {getPaymentDetailsAPIResponse.data?.bankReference}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <>
            {urlContentType === 'image' ? (
              <ProductDetailsGalleryView
                open={isViewReceipt}
                onClose={() => setIsViewReceipt(false)}
                images={[{ src: receiptURL, alt: '', type: 'image' }]}
              />
            ) : (
              <iframe src={receiptURL} title="PDF Viewer" style={{ flex: 1 }} />
            )}
          </>
        )}
      </div>
    </div>
  );
};
